import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Copy, Check, Loader2, RotateCcw } from 'lucide-react';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FooterActions,
  InviteCodeRow,
  InviteCodeText,
  CopyIconButton,
  InviteMessagePreview,
  InviteMessageLoading,
  InviteMessageError,
} from './InviteUserModal.styles';
import { RetryButton } from '../../../../settings/appStyle/global.style';
import { useInviteUser } from '../hooks/useTeamMembers';
import { buildEmailHtml } from '../../../AdminPages/Clients/utils/buildEmailHtml';
import { formatInviteMessageHtml } from '../../../../helper/formatInviteMessage';

const InviteUserModal = ({ isOpen, onClose }) => {
  const [copiedField, setCopiedField] = useState(null);
  const [invite, setInvite] = useState(null);

  const theme = localStorage.getItem('theme');
  const isDark = theme === 'dark';

  const inviteClientMutation = useInviteUser({
    onSuccess: (data) => setInvite(data),
    onError: (err) => {
      window.alert(`Failed to generate invitation${err?.message ? `: ${err.message}` : '.'}`);
    },
  });

  useEffect(() => {
    if (isOpen) {
      setInvite(null);
      inviteClientMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField((current) => (current === field ? null : current)), 1800);
    } catch {
      window.alert('Could not copy to clipboard. Please copy it manually.');
    }
  };

  const handleRetry = () => {
    inviteClientMutation.mutate();
  };

  const previewHtml = invite
    ? buildEmailHtml(formatInviteMessageHtml(invite?.invitationMessage), isDark)
    : '';

  return createPortal(
    <ModalOverlay role="presentation">
      <ModalContent
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-client-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle id="invite-client-modal-title">Invite User</ModalTitle>
          <ModalCloseButton type="button" onClick={onClose} aria-label="Close dialog">
            <X size={18} />
          </ModalCloseButton>
        </ModalHeader>

        <ModalBody>
          {inviteClientMutation.isPending && (
            <InviteMessageLoading>
              <Loader2 />
              Generating invitation…
            </InviteMessageLoading>
          )}

          {inviteClientMutation.isError && !inviteClientMutation.isPending && (
            <InviteMessageError>
              <span>Couldn't generate an invitation for this client.</span>
              <RetryButton type="button" onClick={handleRetry}>
                <RotateCcw />
                Retry
              </RetryButton>
            </InviteMessageError>
          )}

          {invite && !inviteClientMutation.isPending && (
            <>
              <InviteCodeRow>
                <InviteCodeText>{invite.clientCode}</InviteCodeText>
                <CopyIconButton type="button" onClick={() => handleCopy(invite.clientCode, 'code')}>
                  {copiedField === 'code' ? <Check /> : <Copy />}
                  {copiedField === 'code' ? 'Copied' : 'Copy code'}
                </CopyIconButton>
              </InviteCodeRow>

              <InviteMessagePreview dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <FooterActions>
            {invite && (
              <CopyIconButton
                type="button"
                onClick={() => handleCopy(invite.invitationMessage, 'message')}
              >
                {copiedField === 'message' ? <Check /> : <Copy />}
                {copiedField === 'message' ? 'Copied message' : 'Copy invitation message'}
              </CopyIconButton>
            )}
          </FooterActions>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

export default InviteUserModal;
